import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { 
  sendChatMessage, 
  ChatMessage, 
  ChatRequest, 
  TokenUsage,
  FunctionCall 
} from '../api/chat';
import { useCurrentUser } from "../hooks/useCurrentUser";

export interface ChatMessageWithId extends ChatMessage {
  id: string;
  timestamp: Date;
  sender: 'user' | 'agent';
  text: string; // For compatibility with existing ChatComponent
}

export interface UseAccountSetupChatReturn {
  messages: ChatMessageWithId[];
  sendMessage: (message: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearChat: () => void;
  totalTokensUsed: number;
  lastTokenUsage: TokenUsage | null;
  functionCalls: FunctionCall[];
}

export const useAccountSetupChat = (): UseAccountSetupChatReturn => {
  const currentUser = useCurrentUser();
  
  // Local state for conversation
  const [messages, setMessages] = useState<ChatMessageWithId[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hi! I\'m your Account Setup Agent. I can help you configure your account, set up integrations, and answer any questions about getting started with Bagel.',
      text: 'Hi! I\'m your Account Setup Agent. I can help you configure your account, set up integrations, and answer any questions about getting started with Bagel.',
      sender: 'agent',
      timestamp: new Date(),
    }
  ]);
  
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  const [lastTokenUsage, setLastTokenUsage] = useState<TokenUsage | null>(null);
  const [functionCalls, setFunctionCalls] = useState<FunctionCall[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Mutation for sending messages
  const sendMessageMutation = useMutation({
    mutationFn: async (userMessage: string) => {
      console.log('💬 [Chat Hook] Starting message send:', userMessage.substring(0, 50) + '...');
      
      // Clear any previous errors
      setError(null);

      // Create user message
      const userMessageObj: ChatMessageWithId = {
        id: Date.now().toString(),
        role: 'user',
        content: userMessage,
        text: userMessage,
        sender: 'user',
        timestamp: new Date(),
      };

      console.log('📝 [Chat Hook] Created user message object:', {
        id: userMessageObj.id,
        messageLength: userMessage.length
      });

      // Add user message to conversation immediately
      setMessages(prev => [...prev, userMessageObj]);

      // Prepare conversation history for API (exclude the welcome message and current user message)
      const conversationHistory: ChatMessage[] = messages
        .slice(1) // Remove welcome message
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));

      console.log('📚 [Chat Hook] Prepared conversation history:', {
        historyLength: conversationHistory.length,
        totalMessages: messages.length
      });

      // Prepare API request
      const request: ChatRequest = {
        agent: 'account-setup-agent',
        message: userMessage,
        conversation: conversationHistory.length > 0 ? conversationHistory : undefined
      };

      console.log('📤 [Chat Hook] Sending API request...');

      // Send to API
      const response = await sendChatMessage(request, currentUser);
      
      console.log('📥 [Chat Hook] API response received:', {
        success: response.success,
        hasData: !!response.data,
        error: response.error
      });
      
      if (!response.success) {
        console.error('❌ [Chat Hook] API request failed:', response.error);
        throw new Error(response.error || 'Failed to send message');
      }

      return { response, userMessageObj };
    },
    onSuccess: ({ response, userMessageObj }) => {
      console.log('✅ [Chat Hook] Message sent successfully');
      
      if (response.data) {
        console.log('📊 [Chat Hook] Processing response data:', {
          responseLength: response.data.response?.length,
          tokenUsage: response.data.tokenUsage,
          hasFunctionCalls: !!response.data.functionCalls?.length
        });

        // Create agent response message
        const agentMessage: ChatMessageWithId = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.data.response,
          text: response.data.response,
          sender: 'agent',
          timestamp: new Date(),
        };

        console.log('🤖 [Chat Hook] Created agent message:', {
          id: agentMessage.id,
          responseLength: response.data.response.length
        });

        // Add agent message to conversation
        setMessages(prev => [...prev, agentMessage]);

        // Update token usage
        setLastTokenUsage(response.data.tokenUsage);
        const newTotal = totalTokensUsed + response.data.tokenUsage.totalTokens;
        setTotalTokensUsed(newTotal);
        
        console.log('🔢 [Chat Hook] Updated token usage:', {
          thisRequest: response.data.tokenUsage.totalTokens,
          newTotal: newTotal
        });

        // Update function calls if any
        if (response.data.functionCalls && response.data.functionCalls.length > 0) {
          console.log('⚙️ [Chat Hook] Processing function calls:', response.data.functionCalls.length);
          setFunctionCalls(prev => [...prev, ...response.data.functionCalls!]);
        }
      }
    },
    onError: (error: Error) => {
      console.error('❌ [Chat Hook] Message send failed:', error.message);
      setError(error.message);
      
      // Remove the user message that failed to send
      setMessages(prev => {
        console.log('🔄 [Chat Hook] Removing failed user message from conversation');
        return prev.slice(0, -1);
      });
    },
  });

  // Send message function
  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) {
      console.warn('⚠️ [Chat Hook] Attempted to send empty message');
      return;
    }
    
    console.log('🚀 [Chat Hook] sendMessage called with:', message.substring(0, 50) + '...');
    
    try {
      await sendMessageMutation.mutateAsync(message.trim());
    } catch (error) {
      // Error is handled in onError callback
      console.error('💥 [Chat Hook] sendMessage failed:', error);
    }
  }, [sendMessageMutation]);

  // Clear chat function
  const clearChat = useCallback(() => {
    console.log('🧹 [Chat Hook] Clearing chat conversation');
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: 'Hi! I\'m your Account Setup Agent. I can help you configure your account, set up integrations, and answer any questions about getting started with Bagel.',
        text: 'Hi! I\'m your Account Setup Agent. I can help you configure your account, set up integrations, and answer any questions about getting started with Bagel.',
        sender: 'agent',
        timestamp: new Date(),
      }
    ]);
    setTotalTokensUsed(0);
    setLastTokenUsage(null);
    setFunctionCalls([]);
    setError(null);
    console.log('✅ [Chat Hook] Chat cleared successfully');
  }, []);

  return {
    messages,
    sendMessage,
    isLoading: sendMessageMutation.isPending,
    error,
    clearChat,
    totalTokensUsed,
    lastTokenUsage,
    functionCalls,
  };
};
